import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, Component, OnInit } from '@angular/core';
import { TlkBannerComponent } from '@itero/ui-toolkit-angular/banner';
import { ProcedureLoadRxService } from '@modules/order/services/procedure-load-rx.service';
import { RxIntuitiveApplianceDetailsComponent } from '@modules/rx-intuitive-appliance-details/containers/rx-intuitive-appliance-details/rx-intuitive-appliance-details.component';
import { RxIntuitiveInvisalignDetailsComponent } from '@modules/rx-intuitive-invisalign-details/containers/rx-intuitive-invisalign-details/rx-intuitive-invisalign-details.component';
import { RxIntuitiveAttachmentsComponent } from '@modules/rx-intuitive-attachments/containers/rx-intuitive-attachments/rx-intuitive-attachments.component';
import { RxIntuitiveDentureProcedureComponent } from '@modules/rx-intuitive-denture-procedure/containers/rx-intuitive-denture-procedure/rx-intuitive-denture-procedure.component';
import { RxIntuitiveNotesComponent } from '@modules/rx-intuitive-notes/containers/rx-intuitive-notes/rx-intuitive-notes.component';
import { RxIntuitiveTeethOrchestratorContainerComponent } from '@modules/rx-intuitive-teeth-orchestrator';
import { RxIntuitiveOrderComponent } from '@modules/rx-intuitive-order/containers/rx-intuitive-order/rx-intuitive-order.component';
import { RxIntuitiveScanOptionsComponent } from '@modules/rx-intuitive-scan-options/containers/rx-intuitive-scan-options/rx-intuitive-scan-options.component';
import { TranslateModule } from '@ngx-translate/core';
import { RxIntuitiveScanSurgicalGuideComponent } from '@modules/rx-intuitive-scan-surgical-guide/containers/rx-intuitive-scan-surgical-guide/rx-intuitive-scan-surgical-guide.component';
import { RxIntuitiveArchAlignmentComponent } from '@modules/rx-intuitive-arch-alignment/rx-intuitive-arch-alignment.component';
import { BaseDestroyableComponent } from '@shared/base-classes/base-destroyable';
import { RxAttachmentsVisibiltyEnum } from '@shared/models/enums/rx-attachments-visiblity.enum';
import { RxAttachmentsFacade } from '@shared/rx-attachments/rx-attachments.facade';
import { ShellQuery } from '@shared/store/shell/shell-query';
import { combineLatest, Observable } from 'rxjs';
import { map, takeUntil } from 'rxjs/operators';

import { ProcedureSelectedPageFacade } from './procedure-selected-page.facade';
import { RxIntuitiveArtBannersOrchestratorComponent } from '@modules/rx-intuitive-art/containers/rx-intuitive-art-banners-orchestrator/rx-intuitive-art-banners-orchestrator.component';

@Component({
	selector: 'rx-procedure-selected-page',
	templateUrl: './procedure-selected-page.component.html',
	styleUrls: ['./procedure-selected-page.component.scss'],
	providers: [ProcedureSelectedPageFacade, ProcedureLoadRxService, RxAttachmentsFacade],
	changeDetection: ChangeDetectionStrategy.OnPush,
	standalone: true,
	imports: [
		CommonModule,
		RxIntuitiveOrderComponent,
		RxIntuitiveDentureProcedureComponent,
		RxIntuitiveApplianceDetailsComponent,
		RxIntuitiveInvisalignDetailsComponent,
		RxIntuitiveArchAlignmentComponent,
		RxIntuitiveScanOptionsComponent,
		RxIntuitiveScanSurgicalGuideComponent,
		RxIntuitiveTeethOrchestratorContainerComponent,
		RxIntuitiveAttachmentsComponent,
		RxIntuitiveNotesComponent,
		TranslateModule,
		TlkBannerComponent,
		RxIntuitiveArtBannersOrchestratorComponent
	]
})
export class ProcedureSelectedPageComponent extends BaseDestroyableComponent implements OnInit {
	readonly isApplianceProcedureSectionVisible$: Observable<boolean> = this.facade.isApplianceProcedureSectionVisible$;
	readonly isDentureDetailsVisible$: Observable<boolean> = this.facade.isDentureDetailsVisible$;
	readonly isInvisalignDetailsVisible$: Observable<boolean> = this.facade.isInvisalignDetailsVisible$;
	readonly isArtProcedureSelected$: Observable<boolean> = this.facade.isArtProcedureSelected$;
	readonly isReadOnly$: Observable<boolean> = this.shellQuery.isReadOnly$;
	readonly isScanSurgicalGuideSectionVisible$: Observable<boolean> = this.facade.isScanSurgicalGuideSectionVisible$;
	readonly isToothDiagramVisible$: Observable<boolean> = this.facade.isToothDiagramVisible$;
	readonly isSendToIDSMessageVisible$: Observable<boolean> = this.facade.isSendToIDSMessageVisible$;
	readonly isNotesVisible$: Observable<boolean> = this.facade.isNotesVisible$;

	/**
	 * When attachments list + notes are both read-only, stretch inner bodies so the attachment
	 * panel and notes card fill their columns (see `.attachments-notes-row--read-only-both-fill`).
	 */
	readonly attachmentsNotesReadOnlyBothFillRow$: Observable<boolean> = combineLatest([
		this.rxAttachmentsFacade.rxIntuitiveAttachmentsSectionVisibility$,
		this.rxAttachmentsFacade.isAttachmentsReadOnly$,
		this.shellQuery.getReadOnlyState('notes')
	]).pipe(
		map(
			([visibility, attachmentsReadOnly, notesReadOnly]) =>
				visibility === RxAttachmentsVisibiltyEnum.ShowAttachments && attachmentsReadOnly && notesReadOnly
		)
	);

	constructor(
		private readonly facade: ProcedureSelectedPageFacade,
		private readonly procedureLoadRxService: ProcedureLoadRxService,
		private readonly rxAttachmentsFacade: RxAttachmentsFacade,
		private readonly shellQuery: ShellQuery
	) {
		super();
	}

	ngOnInit(): void {
		this.loadInProgressRx();
		this.rxAttachmentsFacade.clearAttachmentsOnHide$().pipe(takeUntil(this.componentAlive$)).subscribe();
	}

	private loadInProgressRx(): void {
		this.procedureLoadRxService.loadProcedureFlowOrder().pipe(takeUntil(this.componentAlive$)).subscribe();
	}
}
